import type { Block, BlockKind, BlockKindFilterValue } from '@/blocks/domain/types';
import { BLOCK_KIND_LABELS, CREATABLE_BLOCK_KINDS, OTHER_BLOCK_KIND_LABEL, OTHER_KIND_FILTER_VALUE } from '@/blocks/domain/types';

export interface BlockKindFilterOption {
  value: BlockKindFilterValue;
  label: string;
}

/**
 * The Block Library kind filter's options: the kinds that actually occur in the current results
 * (not a fixed catalog), so the control never offers a kind that would filter to nothing.
 *
 * Order: creatable kinds first (catalog order), then every other supported kind alphabetically by
 * display label, then one trailing "Other" bucket for blocks whose stored kind the app does not
 * support. `selected` is always kept so a bookmarked `?kind=` never shows a value with no option.
 */
export function buildKindFilterOptions(
  blocks: ReadonlyArray<Pick<Block, 'kind' | 'isSupportedKind'>>,
  selected?: BlockKindFilterValue | 'all'
): BlockKindFilterOption[] {
  const present = new Set<BlockKindFilterValue>();
  for (const block of blocks) {
    present.add(block.isSupportedKind === false ? OTHER_KIND_FILTER_VALUE : block.kind);
  }
  if (selected && selected !== 'all') present.add(selected);

  const creatable = CREATABLE_BLOCK_KINDS.filter((kind) => present.has(kind));
  const rest = (Object.keys(BLOCK_KIND_LABELS) as BlockKind[])
    .filter((kind) => present.has(kind) && !CREATABLE_BLOCK_KINDS.includes(kind))
    .sort((a, b) => BLOCK_KIND_LABELS[a].localeCompare(BLOCK_KIND_LABELS[b]));

  const options: BlockKindFilterOption[] = [...creatable, ...rest].map((kind) => ({ value: kind, label: BLOCK_KIND_LABELS[kind] }));
  if (present.has(OTHER_KIND_FILTER_VALUE)) options.push({ value: OTHER_KIND_FILTER_VALUE, label: OTHER_BLOCK_KIND_LABEL });
  return options;
}
